import { useCallback, useEffect, useRef, useState } from 'react';

import { getItems } from '@/modules/inventory/items/redux/ItemCRUD';

import { RequestMaterialItemOption } from '../types';

interface UseRequestMaterialItemsParams {
  companyId: string;
  skip?: boolean;
  onError?: (message: string) => void;
}

export default function useRequestMaterialItems({
  companyId,
  skip = false,
  onError,
}: UseRequestMaterialItemsParams) {
  const [itemOptions, setItemOptions] = useState<RequestMaterialItemOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchItems = useCallback(async (company: string, search?: string) => {
    if (!company || company === 'global') {
      setItemOptions([]);
      return [];
    }

    if (search === undefined) {
      setLoading(true);
    } else {
      setSearchLoading(true);
    }

    try {
      const response = await getItems({ companyId: company, pageSize: 50, search: search || '' });
      const results = (response.results || []).map((row) => ({
        id: row.id,
        name: row.name,
        code: row.code,
        supplier_id: row.supplier_id || undefined,
        supplier_name: row.supplier_name || undefined,
        unit_id: row.uom_id || undefined,
        unit_name: row.uom_name || undefined,
        last_price: row.last_price || undefined,
      }));

      if (search === undefined) {
        setItemOptions(results);
      }

      return results;
    } catch (_error) {
      onError?.('Không thể tải danh sách vật tư.');
      if (search === undefined) {
        setItemOptions([]);
      }
      return [];
    } finally {
      if (search === undefined) {
        setLoading(false);
      } else {
        setSearchLoading(false);
      }
    }
  }, [onError]);

  useEffect(() => {
    if (skip || !companyId) return;
    void fetchItems(companyId);
  }, [companyId, skip]);

  const searchItems = useCallback((query: string): Promise<RequestMaterialItemOption[]> => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    return new Promise((resolve) => {
      searchTimerRef.current = setTimeout(async () => {
        const results = await fetchItems(companyId, query);
        resolve(results);
      }, 300);
    });
  }, [companyId, fetchItems]);

  const addOption = (option: RequestMaterialItemOption) => {
    setItemOptions((prev) => [...prev, option]);
  };

  return { itemOptions, loading, searchLoading, searchItems, addOption };
}
